import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AppShell from '../components/AppShell';
import {
  Plus, Trash2, Sparkles, Image as ImageIcon, User, Loader2,
  Wand2, Camera, ChevronRight, Layers, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRAMINGS = [
  { value: 'headshot', label: 'Headshot', desc: 'Close-up face & shoulders' },
  { value: 'bust', label: 'Bust', desc: 'Head to chest' },
  { value: 'waist', label: 'Waist Length', desc: 'Head to waist' },
  { value: 'full_length', label: 'Full Length', desc: 'Head to toe' },
];

const ORIENTATIONS = [
  { value: 'portrait', label: 'Portrait', ratio: '3:4' },
  { value: 'landscape', label: 'Landscape', ratio: '16:9' },
  { value: 'square', label: 'Square', ratio: '1:1' },
];

const STYLES = [
  { value: 'hyperrealism', label: 'Hyperrealism', desc: 'Photorealistic, studio quality' },
  { value: 'animated', label: 'Animated', desc: 'Pixar-style 3D render' },
  { value: 'anime', label: 'Anime', desc: 'Japanese animation style' },
  { value: 'stylized', label: 'Stylized', desc: 'Editorial illustration' },
  { value: 'claymation', label: 'Claymation', desc: 'Stop-motion character' },
];

const InfluencersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    prompt: '',
    style: 'hyperrealism',
    framing: 'headshot',
    orientation: 'portrait',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInfluencers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/influencers/`, { headers });
      setInfluencers(data);
    } catch {
      toast.error('Failed to load influencers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInfluencers(); }, [fetchInfluencers]);

  const handleCreate = async () => {
    if (!form.prompt.trim()) return toast.error('Describe your influencer');
    setCreating(true);
    try {
      const { data: influencer } = await axios.post(`${API}/influencers/`, {
        prompt: form.prompt,
        style: { style_type: form.style },
        framing: form.framing,
        orientation: form.orientation,
      }, { headers });

      toast.success(`${influencer.name} created! Generating first image...`);
      setDialogOpen(false);
      setForm({ prompt: '', style: 'hyperrealism', framing: 'headshot', orientation: 'portrait' });

      // Auto-generate first image (async with polling)
      try {
        const { data: job } = await axios.post(`${API}/influencers/${influencer.id}/images/generate`, {
          prompt: form.prompt,
          framing: form.framing,
          orientation: form.orientation,
        }, { headers });

        // Poll in background
        const poll = async () => {
          for (let i = 0; i < 120; i++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const { data: status } = await axios.get(`${API}/influencers/jobs/${job.job_id}/status`, { headers });
              if (status.status === 'completed') {
                toast.success('First image generated!');
                fetchInfluencers();
                return;
              } else if (status.status === 'failed') {
                toast.info('Image generation failed — try again from the detail page');
                return;
              }
            } catch { /* continue */ }
          }
        };
        poll();
      } catch {
        toast.info('Influencer created — generate images from the detail page');
      }
      fetchInfluencers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create influencer');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm('Delete this influencer and all their images?')) return;
    try {
      await axios.delete(`${API}/influencers/${id}`, { headers });
      setInfluencers(prev => prev.filter(i => i.id !== id));
      toast.success('Influencer deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="influencers-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">AI Influencers</h1>
              <p className="text-sm text-white/40 mt-0.5">Create and manage AI-generated characters</p>
            </div>
            <Button
              data-testid="create-influencer-btn"
              onClick={() => setDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white gap-2"
            >
              <Plus className="w-4 h-4" /> New Influencer
            </Button>
          </div>
        </div>

        {/* Grid */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : influencers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-4">
                <User className="w-7 h-7 text-white/20" />
              </div>
              <h3 className="text-lg font-medium text-white/60 mb-1">No influencers yet</h3>
              <p className="text-sm text-white/30 mb-6 max-w-md">
                Describe a character — their look, style, accessories — and Delphi will bring them to life.
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500 gap-2">
                <Sparkles className="w-4 h-4" /> Create Your First Influencer
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {influencers.map(inf => (
                <Link
                  key={inf.id}
                  to={`/influencers/${inf.id}`}
                  data-testid={`influencer-card-${inf.id}`}
                  className="group relative bg-[#12121a] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.12] transition-all duration-200 hover:shadow-lg hover:shadow-black/40"
                >
                  {/* Image */}
                  <div className="aspect-[3/4] bg-[#0e0e16] relative overflow-hidden">
                    {inf.avatar_url ? (
                      <img
                        src={inf.avatar_url}
                        alt={inf.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 h-12 text-white/10" />
                      </div>
                    )}
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                        <span className="text-xs text-white/60 flex items-center gap-1">
                          <Camera className="w-3 h-3" /> {inf.image_count || 0} images
                        </span>
                        <ChevronRight className="w-4 h-4 text-white/50" />
                      </div>
                    </div>
                    {/* Training badge */}
                    {inf.training_status === 'trained' && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                        <Zap className="w-2.5 h-2.5" /> Trained
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-3">
                    <h3 className="font-medium text-sm text-white/90 truncate">{inf.name || 'Unnamed'}</h3>
                    <p className="text-xs text-white/30 mt-0.5 truncate">
                      {inf.niche || inf.style?.style_type || 'AI Influencer'}
                    </p>
                  </div>
                  {/* Delete */}
                  <button
                    data-testid={`delete-influencer-${inf.id}`}
                    onClick={(e) => handleDelete(e, inf.id)}
                    className="absolute top-2 left-2 p-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#14141e] border-white/[0.08] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Create AI Influencer</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Prompt */}
              <div>
                <Label className="text-sm text-white/60 mb-1.5 block">Describe your influencer</Label>
                <Textarea
                  data-testid="influencer-prompt"
                  value={form.prompt}
                  onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                  placeholder="A 25-year-old woman with long auburn hair, green eyes, wearing a cream knit sweater with gold hoop earrings, standing in front of a sunlit cafe..."
                  className="bg-white/[0.04] border-white/[0.08] text-white min-h-[100px] placeholder:text-white/20 focus:border-blue-500/40"
                />
                <p className="text-[11px] text-white/25 mt-1.5">
                  Include details: name, age, hair, eyes, clothing, accessories, jewelry, background
                </p>
              </div>

              {/* Style */}
              <div>
                <Label className="text-sm text-white/60 mb-1.5 block">Visual Style</Label>
                <div className="grid grid-cols-5 gap-2">
                  {STYLES.map(s => (
                    <button
                      key={s.value}
                      data-testid={`style-${s.value}`}
                      onClick={() => setForm(f => ({ ...f, style: s.value }))}
                      className={`p-2 rounded-lg border text-center transition-all text-xs ${
                        form.style === s.value
                          ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                          : 'border-white/[0.06] bg-white/[0.02] text-white/50 hover:border-white/[0.12]'
                      }`}
                    >
                      <div className="font-medium">{s.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Framing */}
              <div>
                <Label className="text-sm text-white/60 mb-1.5 block">Image Framing</Label>
                <div className="grid grid-cols-4 gap-2">
                  {FRAMINGS.map(f => (
                    <button
                      key={f.value}
                      data-testid={`framing-${f.value}`}
                      onClick={() => setForm(prev => ({ ...prev, framing: f.value }))}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        form.framing === f.value
                          ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                          : 'border-white/[0.06] bg-white/[0.02] text-white/50 hover:border-white/[0.12]'
                      }`}
                    >
                      <div className="text-xs font-medium">{f.label}</div>
                      <div className="text-[10px] opacity-60 mt-0.5">{f.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Orientation */}
              <div>
                <Label className="text-sm text-white/60 mb-1.5 block">Orientation</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ORIENTATIONS.map(o => (
                    <button
                      key={o.value}
                      data-testid={`orientation-${o.value}`}
                      onClick={() => setForm(prev => ({ ...prev, orientation: o.value }))}
                      className={`p-2.5 rounded-lg border text-center transition-all ${
                        form.orientation === o.value
                          ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                          : 'border-white/[0.06] bg-white/[0.02] text-white/50 hover:border-white/[0.12]'
                      }`}
                    >
                      <div className="text-sm font-medium">{o.label}</div>
                      <div className="text-[10px] opacity-60">{o.ratio}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Create Button */}
              <Button
                data-testid="submit-create-influencer"
                onClick={handleCreate}
                disabled={creating || !form.prompt.trim()}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11 gap-2"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                ) : (
                  <><Wand2 className="w-4 h-4" /> Create Influencer</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
};

export default InfluencersPage;
